import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { 
  Users, 
  Clock, 
  Eye, 
  MousePointer, 
  Calendar, 
  Heart, 
  TrendingUp, 
  Download,
  Database,
  PlayCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Target
} from 'lucide-react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface AudienceAnalysisProps {
  dateRange: {
    from: Date;
    to: Date;
  };
  projectId?: string;
}

export function AudienceAnalysis({ dateRange, projectId }: AudienceAnalysisProps) {
  const analytics = useAnalytics(projectId);
  const { data: audienceData, isLoading, error } = analytics.useAudience(dateRange);
  
  // GA4 Audience Export functionality
  const { data: audiencesList } = analytics.useListAudiences();
  const createExportMutation = analytics.useCreateAudienceExport();
  
  const [selectedAudience, setSelectedAudience] = useState<string | null>(null);
  const [currentExportId, setCurrentExportId] = useState<string | null>(null);
  const [showAllAudiences, setShowAllAudiences] = useState(false);
  const [audiencesPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Check export status only if we have an export ID
  const { data: exportStatus } = analytics.useAudienceExportStatus(currentExportId || undefined);
  const { data: exportData } = analytics.useAudienceExportData(
    currentExportId || undefined,
    selectedAudience || undefined,
    exportStatus?.status?.state === 'ACTIVE'
  );
  

  
  const dateRangeText = `${dateRange.from.toLocaleDateString()} - ${dateRange.to.toLocaleDateString()}`;
  const isProjectSelected = !!projectId;

  // Use real data if available, otherwise fallback to mock data
  const audience = audienceData?.audience;
  
  // Use enhanced export data if available for more detailed demographics
  const enhancedData = exportData?.data?.enhancedAnalytics;
  
  const handleCreateExport = async (audienceId: string) => {
    try {
      setSelectedAudience(audienceId);
      const result = await createExportMutation.mutateAsync({
        audienceId,
        dimensions: ['deviceId', 'userId', 'isAdsPersonalizationAllowed'] // Use valid GA4 Audience Export dimensions
      });
      
      // Extract export ID from the response - try multiple possible paths
      let exportId = null;
      if (result.export?.name) {
        exportId = result.export.name.split('/').pop();
      } else if (result.export?.response?.name) {
        exportId = result.export.response.name.split('/').pop();
      } else if (result.name) {
        exportId = result.name.split('/').pop();
      } else if (result.response?.name) {
        exportId = result.response.name.split('/').pop();
      }
      setCurrentExportId(exportId);
    } catch (error) {
      console.error('Failed to create audience export:', error);
    }
  };

  const audienceMetrics = [
    {
      title: 'Total Users',
      value: audience?.totalUsers?.toLocaleString() || '24,567',
      change: '+12.5%', // TODO: Calculate change from previous period
      icon: Users,
      color: 'blue'
    },
    {
      title: 'New Users',
      value: audience?.newUsers?.toLocaleString() || '18,234',
      change: '+15.2%', // TODO: Calculate change from previous period
      icon: Heart,
      color: 'green'
    },
    {
      title: 'Returning Users',
      value: audience?.returningUsers?.toLocaleString() || '6,333',
      change: '+8.1%', // TODO: Calculate change from previous period
      icon: Calendar,
      color: 'purple'
    },
    {
      title: 'Avg. Session Duration',
      value: audience?.avgSessionDuration ? `${Math.floor(audience.avgSessionDuration / 60)}m ${audience.avgSessionDuration % 60}s` : '3m 24s',
      change: '+5.7%', // TODO: Calculate change from previous period
      icon: Clock,
      color: 'orange'
    }
  ];

  const ageGroups = (() => {
    // Use enhanced data from audience export first
    if (enhancedData?.ageBreakdown?.length > 0) {
      const totalUsers = enhancedData.ageBreakdown.reduce((sum: number, item: any) => sum + item.users, 0);
      return enhancedData.ageBreakdown.map((item: any) => ({
        ...item,
        percentage: totalUsers > 0 ? (item.users / totalUsers) * 100 : 0
      }));
    }
    // Use real GA4 audience data if available
    if (audience?.ageGroups?.length > 0) {
      return audience.ageGroups;
    }
    // Fallback to mock data
    return [
      { age: '18-24', users: 3456, percentage: 14.1 },
      { age: '25-34', users: 8234, percentage: 33.5 },
      { age: '35-44', users: 6789, percentage: 27.6 },
      { age: '45-54', users: 4321, percentage: 17.6 },
      { age: '55-64', users: 1543, percentage: 6.3 },
      { age: '65+', users: 224, percentage: 0.9 }
    ];
  })();

  const genderData = (() => {
    let data;
    // Use enhanced data from audience export first
    if (enhancedData?.genderBreakdown?.length > 0) {
      const totalUsers = enhancedData.genderBreakdown.reduce((sum: number, item: any) => sum + item.users, 0);
      data = enhancedData.genderBreakdown.map((item: any) => ({
        ...item,
        percentage: totalUsers > 0 ? (item.users / totalUsers) * 100 : 0
      }));
    } 
    // Use real GA4 audience data if available
    else if (audience?.genderData?.length > 0) {
      data = audience.genderData;
    }
    // Fallback to mock data
    else {
      data = [
        { gender: 'Female', users: 13456, percentage: 54.8 },
        { gender: 'Male', users: 10234, percentage: 41.6 },
        { gender: 'Other', users: 877, percentage: 3.6 }
      ];
    }
    
    return data.map((item: any) => ({
      ...item,
      color: item.gender === 'Female' || item.gender === 'female' ? 'bg-pink-500' : 
             item.gender === 'Male' || item.gender === 'male' ? 'bg-blue-500' : 'bg-gray-500'
    }));
  })();

  const interests = (() => {
    // Use real GA4 audience data if available
    if (audience?.interests?.length > 0) {
      return audience.interests;
    }
    // Fallback to mock data
    return [
      { interest: 'Technology', users: 8934, percentage: 36.4 },
      { interest: 'Business', users: 6721, percentage: 27.4 },
      { interest: 'Marketing', users: 4567, percentage: 18.6 },
      { interest: 'Design', users: 2890, percentage: 11.8 },
      { interest: 'Finance', users: 1455, percentage: 5.9 }
    ];
  })();

  const userBehavior = (() => {
    // Use real GA4 behavior data if available
    if (audience?.userBehavior?.length > 0) {
      return audience.userBehavior.slice(0, 4).map((behavior: any) => ({
        metric: behavior.eventName || 'Event',
        value: behavior.eventCount?.toLocaleString() || '0',
        change: behavior.isKeyEvent ? 'Key Event' : 'Regular Event'
      }));
    }
    
    // Otherwise use calculated metrics from audience data
    return [
      { metric: 'Pages per Session', value: '3.2', change: '+0.3' },
      { metric: 'Bounce Rate', value: audience?.bounceRate ? `${(audience.bounceRate * 100).toFixed(1)}%` : '42.3%', change: '-2.1%' },
      { metric: 'Session Duration', value: audience?.avgSessionDuration ? `${Math.floor(audience.avgSessionDuration / 60)}m ${audience.avgSessionDuration % 60}s` : '3m 24s', change: '+15s' },
      { metric: 'Return Visitor Rate', value: audience?.totalUsers ? `${((audience.returningUsers / audience.totalUsers) * 100).toFixed(1)}%` : '25.8%', change: '+1.2%' }
    ];
  })();

  const topDevices = (() => {
    // Use enhanced data from audience export first
    if (enhancedData?.deviceBreakdown?.length > 0) {
      const totalUsers = enhancedData.deviceBreakdown.reduce((sum: number, item: any) => sum + item.users, 0);
      return enhancedData.deviceBreakdown.map((item: any) => ({
        ...item,
        percentage: totalUsers > 0 ? (item.users / totalUsers) * 100 : 0
      }));
    }
    // Use real GA4 audience data if available
    if (audience?.deviceBreakdown?.length > 0) {
      return audience.deviceBreakdown;
    }
    // Fallback to mock data
    return [
      { device: 'iPhone', users: 6234, percentage: 25.4 },
      { device: 'Windows PC', users: 5432, percentage: 22.1 },
      { device: 'Android', users: 4321, percentage: 17.6 },
      { device: 'MacBook', users: 3456, percentage: 14.1 },
      { device: 'iPad', users: 2345, percentage: 9.5 }
    ];
  })();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Audience Analysis</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">Understand your audience demographics and behavior</p>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{dateRangeText}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Audience Analysis</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">Understand your audience demographics and behavior</p>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{dateRangeText}</div>
        </div>
        <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400">Error loading audience data</p>
            <p className="text-sm text-red-500 dark:text-red-400 mt-1">{error.message}</p>
            {error.message.includes('404') && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                The audience data endpoint is not available. This feature may require additional configuration.
              </p>
            )}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Audience Analysis</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">Understand your audience demographics and behavior</p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {dateRangeText} {isProjectSelected && `• Project: ${projectId}`}
          </div>
          {audienceData?.dataSource && (
            <Badge variant={audienceData.dataSource === 'ga4_real' ? 'success' : 'secondary'}>
              {audienceData.dataSource === 'ga4_real' ? 'Real Data' : 'Sample Data'}
            </Badge>
          )}
        </div>
      </div>

      {/* GA4 Audience Export Section */}
      {((audiencesList?.audiences && audiencesList.audiences.length > 0) || audienceData?.audience?.exportCapability === 'insufficient_permissions') && (
        <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-blue-200 dark:border-blue-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3">
                <Database className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">GA4 Audience Insights</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Expand audience segments to see detailed demographics and behavior
                </p>
              </div>
            </div>
            {audiencesList?.audiences ? (
              <Badge variant="success">
                {audiencesList.audiences.length} Audiences Available
              </Badge>
            ) : (
              <Badge variant="warning">
                Export Permissions Required
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Available Audiences */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 dark:text-white">Available Audiences</h4>
                {audiencesList?.audiences?.length > audiencesPerPage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowAllAudiences(!showAllAudiences);
                      setCurrentPage(1); // Reset to first page when toggling
                    }}
                  >
                    {showAllAudiences ? 'Show Less' : `Show All (${audiencesList.audiences.length})`}
                  </Button>
                )}
              </div>
              
              {/* Permissions message */}
              {audienceData?.audience?.exportCapability === 'insufficient_permissions' && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                  <div className="flex items-center">
                    <div className="w-5 h-5 text-yellow-600 mr-2">⚠️</div>
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Permissions Required</p>
                      <p className="text-xs text-yellow-700 mt-1">
                        {audienceData.audience.exportPermissionsNote}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                {audiencesList?.audiences?.length > 0 ? (
                  (() => {
                    const audiences = audiencesList.audiences;
                    const totalAudiences = audiences.length;
                    const startIndex = showAllAudiences ? (currentPage - 1) * audiencesPerPage : 0;
                    const endIndex = showAllAudiences ? Math.min(startIndex + audiencesPerPage, totalAudiences) : Math.min(audiencesPerPage, totalAudiences);
                    const displayedAudiences = showAllAudiences ? audiences.slice(startIndex, endIndex) : audiences.slice(0, audiencesPerPage);
                    
                    return (
                      <>
                        {displayedAudiences.map((audience: any, index: number) => {
                          const audienceId = audience.name.split('/').pop();
                          const isSelected = selectedAudience === audienceId;
                          
                          return (
                            <div key={audienceId} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 hover:border-blue-200 dark:hover:border-blue-700 transition-colors">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900 dark:text-white">{audience.displayName}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  ID: {audienceId}
                                  {audience.description && (
                                    <span className="ml-2">• {audience.description}</span>
                                  )}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant={isSelected ? "default" : "outline"}
                                onClick={() => handleCreateExport(audienceId)}
                                disabled={createExportMutation.isPending || audienceData?.audience?.exportCapability === 'insufficient_permissions'}
                                className="ml-3"
                              >
                                {createExportMutation.isPending && isSelected ? (
                                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                  <Database className="w-4 h-4 mr-2" />
                                )}
                                Expand
                              </Button>
                            </div>
                          );
                        })}
                        
                        {/* Pagination for all audiences view */}
                        {showAllAudiences && totalAudiences > audiencesPerPage && (
                          <div className="flex items-center justify-between pt-4">
                            <div className="text-sm text-gray-500">
                              Showing {startIndex + 1}-{endIndex} of {totalAudiences} audiences
                            </div>
                            <div className="flex space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                              >
                                Previous
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(Math.min(Math.ceil(totalAudiences / audiencesPerPage), currentPage + 1))}
                                disabled={currentPage >= Math.ceil(totalAudiences / audiencesPerPage)}
                              >
                                Next
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()
                ) : (
                  <div className="p-6 text-center bg-white rounded-lg border border-dashed border-gray-300">
                    <Database className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      {audienceData?.audience?.exportCapability === 'insufficient_permissions' 
                        ? 'Audience expansion requires additional permissions'
                        : 'No audiences available for expansion'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Expanded Audience View */}
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">Expanded Audience View</h4>
              {currentExportId ? (
                <div className="space-y-4">
                  {/* Status */}
                                      <div className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Expansion Status</span>
                      <div className="flex items-center">
                        {exportStatus?.status?.state === 'CREATING' && (
                          <>
                            <Loader2 className="w-4 h-4 text-blue-500 animate-spin mr-2" />
                            <span className="text-sm text-blue-600">Expanding...</span>
                          </>
                        )}
                        {exportStatus?.status?.state === 'ACTIVE' && (
                          <>
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                            <span className="text-sm text-green-600">Expanded</span>
                          </>
                        )}
                        {exportStatus?.status?.state === 'FAILED' && (
                          <>
                            <XCircle className="w-4 h-4 text-red-500 mr-2" />
                            <span className="text-sm text-red-600">Failed</span>
                          </>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Analysis ID: {currentExportId}
                    </p>
                  </div>

                  {/* Enhanced Audience Data Preview */}
                  {exportData?.data?.enhancedAnalytics && (
                    <div className="space-y-4">
                      <div className="p-3 bg-white rounded-lg border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Detailed Analytics</span>
                          <Badge variant="success">
                            {exportData.data.rowCount} Users
                          </Badge>
                        </div>
                        
                        {/* Quick Stats */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="text-xs">
                            <span className="text-gray-500">Countries: </span>
                            <span className="font-medium">{exportData.data.enhancedAnalytics.topCountries?.length || 0}</span>
                          </div>
                          <div className="text-xs">
                            <span className="text-gray-500">Key Events: </span>
                            <span className="font-medium">{exportData.data.enhancedAnalytics.keyEvents?.length || 0}</span>
                          </div>
                        </div>
                        
                        {/* Top Countries */}
                        {exportData.data.enhancedAnalytics.topCountries?.slice(0, 3).map((country: any, index: number) => (
                          <div key={index} className="flex justify-between text-xs py-1 border-b border-gray-100 last:border-0">
                            <span className="text-gray-700">{country.country}</span>
                            <span className="font-medium">{country.users.toLocaleString()} users</span>
                          </div>
                        ))}
                      </div>
                      
                      {/* Device Breakdown */}
                      {exportData.data.enhancedAnalytics.deviceBreakdown?.length > 0 && (
                        <div className="p-3 bg-white rounded-lg border">
                          <h5 className="text-sm font-medium mb-2">Device Categories</h5>
                          {exportData.data.enhancedAnalytics.deviceBreakdown.map((device: any, index: number) => (
                            <div key={index} className="flex justify-between text-xs py-1">
                              <span className="text-gray-700">{device.device}</span>
                              <span className="font-medium">{device.users.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Key Events */}
                      {exportData.data.enhancedAnalytics.keyEvents?.length > 0 && (
                        <div className="p-3 bg-white rounded-lg border">
                          <h5 className="text-sm font-medium mb-2">Key Events</h5>
                          {exportData.data.enhancedAnalytics.keyEvents.slice(0, 3).map((event: any, index: number) => (
                            <div key={index} className="flex justify-between text-xs py-1">
                              <span className="text-gray-700">{event.eventName}</span>
                              <span className="font-medium">{event.keyEvents} conversions</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Basic audience data fallback */}
                  {exportData?.data && !exportData.data.enhancedAnalytics && (
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Basic Analysis Data</span>
                        <Badge variant="secondary">
                          {exportData.data.rowCount} Users
                        </Badge>
                      </div>
                      
                      {exportData.data.audienceRows?.slice(0, 3).map((row: any, index: number) => (
                        <div key={index} className="text-xs text-gray-600 py-1 border-b border-gray-100 last:border-0">
                          Device ID: {row.dimensionValues?.[0]?.value || 'N/A'}
                        </div>
                      ))}
                      
                      {exportData.data.rowCount > 3 && (
                        <p className="text-xs text-gray-500 mt-2">
                          +{exportData.data.rowCount - 3} more users...
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                                  <div className="p-6 text-center bg-white rounded-lg border border-dashed border-gray-300">
                    <Database className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Select an audience to expand and analyze</p>
                  </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Enhanced Audience Insights from Expansion */}
      {enhancedData && (
        <div className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg border border-green-200 dark:border-green-700">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-3">
                <Target className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Enhanced Audience Insights</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Real audience-specific data from GA4</p>
              </div>
            </div>
            <Badge variant="success">Real GA4 Audience Data</Badge>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Top Countries */}
            {enhancedData.topCountries?.length > 0 && (
              <Card className="p-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Top Countries</h4>
                <div className="space-y-3">
                  {enhancedData.topCountries.slice(0, 5).map((country: any, index: number) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300">{country.country}</span>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900 dark:text-white">{country.users.toLocaleString()}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{country.sessions.toLocaleString()} sessions</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Traffic Sources */}
            {enhancedData.trafficSources?.length > 0 && (
              <Card className="p-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Traffic Sources</h4>
                <div className="space-y-3">
                  {enhancedData.trafficSources.slice(0, 5).map((source: any, index: number) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300">{source.channel}</span>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900 dark:text-white">{source.users.toLocaleString()}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{source.sessions.toLocaleString()} sessions</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Key Events */}
            {enhancedData.keyEvents?.length > 0 && (
              <Card className="p-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Key Events</h4>
                <div className="space-y-3">
                  {enhancedData.keyEvents.slice(0, 5).map((event: any, index: number) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300">{event.eventName}</span>
                      <div className="text-right">
                        <div className="font-semibold text-green-600 dark:text-green-400">{event.keyEvents}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{event.eventCount} total</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Audience Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {audienceMetrics.map((metric, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{metric.title}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{metric.value}</p>
                <div className="flex items-center mt-2">
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">{metric.change}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">vs last period</span>
                </div>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                metric.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
                metric.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
                metric.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30' :
                'bg-orange-100 dark:bg-orange-900/30'
              }`}>
                <metric.icon className={`w-6 h-6 ${
                  metric.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                  metric.color === 'green' ? 'text-green-600 dark:text-green-400' :
                  metric.color === 'purple' ? 'text-purple-600 dark:text-purple-400' :
                  'text-orange-600 dark:text-orange-400'
                }`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Age Demographics */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Age Demographics</h3>
          <div className="space-y-4">
            {ageGroups.map((group: any, index: number) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">{group.age}</p>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${group.percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="ml-4 text-right">
                  <p className="font-semibold text-gray-900 dark:text-white">{group.users.toLocaleString()}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{group.percentage}%</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Gender Distribution */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Gender Distribution</h3>
          <div className="space-y-4">
            {genderData.map((gender: any, index: number) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center flex-1">
                  <div className={`w-3 h-3 rounded-full ${gender.color} mr-3`}></div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{gender.gender}</p>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                      <div 
                        className={`h-2 rounded-full ${gender.color}`}
                        style={{ width: `${gender.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                <div className="ml-4 text-right">
                  <p className="font-semibold text-gray-900 dark:text-white">{gender.users.toLocaleString()}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{gender.percentage}%</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Interests */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Interests</h3>
          <div className="space-y-4">
            {interests.map((interest: any, index: number) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">{interest.interest}</p>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                    <div 
                      className="bg-purple-600 dark:bg-purple-500 h-2 rounded-full" 
                      style={{ width: `${interest.percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="ml-4 text-right">
                  <p className="font-semibold text-gray-900 dark:text-white">{interest.users.toLocaleString()}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{interest.percentage}%</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* User Behavior */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">User Behavior</h3>
          <div className="space-y-4">
            {userBehavior.map((behavior: any, index: number) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center mr-3">
                    {index === 0 && <Eye className="w-4 h-4 text-gray-600 dark:text-gray-400" />}
                    {index === 1 && <MousePointer className="w-4 h-4 text-gray-600 dark:text-gray-400" />}
                    {index === 2 && <Clock className="w-4 h-4 text-gray-600 dark:text-gray-400" />}
                    {index === 3 && <Calendar className="w-4 h-4 text-gray-600 dark:text-gray-400" />}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{behavior.metric}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Current period</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900 dark:text-white">{behavior.value}</p>
                  <p className="text-sm text-green-600 dark:text-green-400">{behavior.change}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Top Devices */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Devices</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {topDevices.map((device: any, index: number) => (
            <div key={index} className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="font-medium text-gray-900 dark:text-white mb-2">{device.device}</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">{device.users.toLocaleString()}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{device.percentage}%</p>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                <div 
                  className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full" 
                  style={{ width: `${device.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Audience Trends Chart */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Audience Trends Over Time</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">Daily audience growth and composition</p>
          </div>
          {audience?.dataSource && (
            <Badge variant={audience.dataSource === 'ga4_real' ? 'success' : 'secondary'}>
              {audience.dataSource === 'ga4_real' ? 'Real-time Data' : 'Sample Data'}
            </Badge>
          )}
        </div>
        
        {audience?.audienceTrends?.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={audience.audienceTrends}>
                <defs>
                  <linearGradient id="totalUsersGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="newUsersGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="sessionsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#6B7280"
                  fontSize={12}
                  tickFormatter={(value) => {
                    // If the value is already formatted (e.g., "Jan 15"), return it as is
                    if (typeof value === 'string' && value.includes(' ')) {
                      return value;
                    }
                    // Otherwise, try to parse it as a date
                    const date = new Date(value);
                    if (isNaN(date.getTime())) {
                      return value; // Return the original value if it can't be parsed
                    }
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  }}
                />
                <YAxis stroke="#6B7280" fontSize={12} />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                  }}
                  labelFormatter={(value) => {
                    // If the value is already formatted (e.g., "Jan 15"), try to parse it back to a full date
                    if (typeof value === 'string' && value.includes(' ')) {
                      // Try to parse formatted date like "Jan 15"
                      const currentYear = new Date().getFullYear();
                      const date = new Date(`${value} ${currentYear}`);
                      if (!isNaN(date.getTime())) {
                        return date.toLocaleDateString('en-US', { 
                          weekday: 'long',
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        });
                      }
                      return value; // Return as is if parsing fails
                    }
                    // Otherwise, try to parse it as a date
                    const date = new Date(value);
                    if (isNaN(date.getTime())) {
                      return value; // Return the original value if it can't be parsed
                    }
                    return date.toLocaleDateString('en-US', { 
                      weekday: 'long',
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    });
                  }}
                  formatter={(value: any, name: string) => [
                    typeof value === 'number' ? value.toLocaleString() : value,
                    name === 'totalUsers' ? 'Total Users' :
                    name === 'newUsers' ? 'New Users' :
                    name === 'sessions' ? 'Sessions' : name
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="totalUsers"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  fill="url(#totalUsersGradient)"
                  name="totalUsers"
                />
                <Area
                  type="monotone"
                  dataKey="newUsers"
                  stroke="#10B981"
                  strokeWidth={2}
                  fill="url(#newUsersGradient)"
                  name="newUsers"
                />
                <Area
                  type="monotone"
                  dataKey="sessions"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  fill="url(#sessionsGradient)"
                  name="sessions"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 bg-gray-50 dark:bg-gray-800 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <TrendingUp className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
              <p className="text-gray-500 dark:text-gray-400">
                {isLoading ? 'Loading audience trends...' : 'No trend data available for this period'}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                Try selecting a different date range or check back later
              </p>
            </div>
          </div>
        )}
        
        {/* Legend */}
        <div className="flex items-center justify-center space-x-6 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
            <span className="text-sm text-gray-600 dark:text-gray-300">Total Users</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            <span className="text-sm text-gray-600 dark:text-gray-300">New Users</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
            <span className="text-sm text-gray-600 dark:text-gray-300">Sessions</span>
          </div>
        </div>
      </Card>
    </div>
  );
} 